import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '@workspace/public-utils'
import toast from 'react-hot-toast';
import { Seo } from '../../components/Seo';

interface ProfileData {
  id: string;
  name: string;
  username?: string;
  mobile_number?: string;
  role: string;
  provider: string;
  created_at: string;
  subscription_tier?: string;
  subscription_status?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const seo = <Seo title="프로필 관리" canonicalPath="/dashboard/profile" noindex />;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile_number: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/users/profile/me');
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        mobile_number: response.data.mobile_number || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err: any) {
      console.error('Failed to fetch profile:', err);
      toast.error(err.response?.data?.detail || '프로필 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password change
    if (formData.new_password) {
      if (!formData.current_password) {
        toast.error('비밀번호를 변경하려면 현재 비밀번호를 입력해주세요.');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('새 비밀번호가 일치하지 않습니다.');
        return;
      }
      if (formData.new_password.length < 8) {
        toast.error('새 비밀번호는 최소 8자 이상이어야 합니다.');
        return;
      }
    }

    try {
      setSaving(true);
      const updateData: any = {
        mobile_number: formData.mobile_number
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const response = await apiClient.put('/api/v1/users/profile/me', updateData);

      setProfile(response.data);
      toast.success('프로필이 성공적으로 업데이트되었습니다.');
      // Clear password fields
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      toast.error(err.response?.data?.detail || '프로필 업데이트에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('비밀번호를 입력해주세요.');
      return;
    }

    try {
      setSaving(true);
      await apiClient.delete('/api/v1/users/profile/me', {
        params: { password: deletePassword },
      });

      // Clear token and redirect to home
      logout();
      toast.success('계정이 삭제되었습니다.');
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err: any) {
      console.error('Failed to delete account:', err);
      toast.error(err.response?.data?.detail || '계정 삭제에 실패했습니다.');
      setDeleteDialogOpen(false);
      setDeletePassword('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        {seo}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isEmailProvider = profile?.provider === 'email';

  return (
    <>
      {seo}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6">
        <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">프로필 관리</h1>

        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">기본 정보</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {profile?.username && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">사용자 ID</label>
                <input
                  type="text"
                  value={profile.username}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">사용자 ID는 변경할 수 없습니다</p>
              </div>
            )}
            
            {/* Email no longer displayed */}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">이름</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                disabled
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">이름은 변경할 수 없습니다</p>
            </div>

            {/* Nickname removed */}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">휴대폰 번호</label>
              <input
                type="text"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                placeholder="010-1234-5678"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">형식: 010-1234-5678</p>
            </div>
          </div>

          {/* Subscription Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">구독 정보</h2>
          
          <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-4 mb-4">
            {profile?.subscription_tier ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">현재 플랜</label>
                  <div className="text-lg font-semibold text-blue-600">{profile.subscription_tier}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">상태</label>
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                    profile.subscription_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}>
                    {profile.subscription_status === 'active' ? '활성' : 
                     profile.subscription_status === 'expired' ? '만료됨' : 
                     profile.subscription_status === 'cancelled' ? '취소됨' : '비활성'}
                  </div>
                </div>
                {profile.subscription_start_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">시작일</label>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(profile.subscription_start_date).toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                )}
                {profile.subscription_end_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">종료일</label>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(profile.subscription_end_date).toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600 dark:text-gray-400 mb-2">현재 구독 중인 플랜이 없습니다</p>
              </div>
            )}
            
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate('/plans')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
              >
                {profile?.subscription_tier ? '플랜 변경하기' : '플랜 구독하기'}
              </button>
            </div>
          </div>

            {isEmailProvider && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>
              <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">비밀번호 변경</h2>
              
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">현재 비밀번호</label>
                  <input
                    type="password"
                    name="current_password"
                    value={formData.current_password}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">새 비밀번호</label>
                    <input
                      type="password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">최소 8자 이상</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">새 비밀번호 확인</label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-4 justify-between mt-6">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
            {isEmailProvider && (
              <button
                type="button"
                onClick={() => setDeleteDialogOpen(true)}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 transition-colors"
              >
                계정 삭제
              </button>
            )}
          </div>
        </form>

        <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            계정 생성일: {new Date(profile?.created_at || '').toLocaleString('ko-KR')}
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">계정 삭제 확인</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              계정을 삭제하면 모든 데이터가 삭제되며 다시 로그인할 수 없습니다.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">비밀번호 확인</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                placeholder="비밀번호를 입력하세요"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeletePassword('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={saving || !deletePassword}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePage;
